// The one HTTP client. Every backend call goes through `api` — it adds the
// portal token, turns error bodies into Errors carrying `status` and `detail`,
// and treats a 401 on a request that DID carry a token as "the session ended".
//
//   api.get(path)  api.post(path, body)  api.put(path, body)  api.patch(path, body)  api.del(path)
//   api.upload(path, file, { onProgress, signal, md5 }) -> Promise<json>   raw-body POST with progress
//   api.download(path, filename)                                   authenticated download
//   api.putPart(url, body, { headers, onProgress, signal }) -> Promise<void>
//                  a PUT to a presigned S3 URL (direct uploads): NO portal token, absolute URL;
//                  rejects with `status` (0 = network error, which is also how a CORS refusal looks)
//                  and `storage: true`
//
// `path` is relative to /api: api.get("/projects") fetches /api/projects.
// (Pattern from data-portal frontend/src/api.js, much reduced.)

export const API_BASE = "/api";
const TOKEN_KEY = "token";

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode: the session simply does not persist */ }
}

let sessionExpiredHandler = () => {};

/** Registered by AuthProvider: clears the user so ProtectedRoute routes to /login. */
export function setSessionExpiredHandler(fn) {
  sessionExpiredHandler = fn;
}

function httpError(status, statusText, data) {
  const detail = data && data.detail;
  let message;
  if (typeof detail === "string") message = detail;
  else if (Array.isArray(detail)) {
    // FastAPI validation errors: [{loc, msg}, ...]
    message = detail.map(d => [d.loc && d.loc.slice(1).join("."), d.msg].filter(Boolean).join(": ")).join("; ");
  } else if (detail && typeof detail === "object") message = detail.message;
  const err = new Error(message || `HTTP ${status}${statusText ? ` ${statusText}` : ""}`);
  err.status = status;
  err.detail = detail;
  return err;
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function request(method, path, body) {
  const hadToken = !!getToken();
  const headers = { Accept: "application/json", ...authHeaders() };
  const init = { method, headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const res = await fetch(API_BASE + path, init);
  const text = await res.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = null; }
  }
  if (res.status === 401 && hadToken) {
    setToken(null);
    sessionExpiredHandler();
  }
  if (!res.ok) throw httpError(res.status, res.statusText, data);
  return data;
}

function upload(path, file, { onProgress, signal, filename, md5 } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const sep = path.includes("?") ? "&" : "?";
    xhr.open("POST", `${API_BASE}${path}${sep}filename=${encodeURIComponent(filename || file.name)}`);
    const t = getToken();
    if (t) xhr.setRequestHeader("Authorization", `Bearer ${t}`);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    if (md5) xhr.setRequestHeader("X-Content-MD5", md5);
    if (onProgress) {
      xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    }
    xhr.onload = () => {
      let data = null;
      try { data = JSON.parse(xhr.responseText); } catch { data = null; }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else {
        if (xhr.status === 401 && t) { setToken(null); sessionExpiredHandler(); }
        reject(httpError(xhr.status, xhr.statusText, data));
      }
    };
    xhr.onerror = () => reject(new Error("The upload was interrupted (network error)."));
    xhr.onabort = () => reject(new Error("The upload was cancelled."));
    if (signal) signal.addEventListener("abort", () => xhr.abort());
    xhr.send(file);
  });
}

function putPart(url, body, { headers = {}, onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    if (onProgress) {
      xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(e.loaded); };
    }
    const fail = (status, message) => reject(Object.assign(new Error(message), { status, storage: true }));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else fail(xhr.status, `The storage refused the part (HTTP ${xhr.status}).`);
    };
    xhr.onerror = () => fail(0, "The storage could not be reached (network error).");
    xhr.onabort = () => fail(-1, "The upload was cancelled.");
    if (signal) signal.addEventListener("abort", () => xhr.abort());
    xhr.send(body);
  });
}

async function download(path, filename) {
  const res = await fetch(API_BASE + path, { headers: authHeaders() });
  if (!res.ok) {
    let data = null;
    try { data = await res.json(); } catch { data = null; }
    throw httpError(res.status, res.statusText, data);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const api = {
  get:   path       => request("GET", path),
  post:  (path, b)  => request("POST", path, b === undefined ? {} : b),
  put:   (path, b)  => request("PUT", path, b),
  patch: (path, b)  => request("PATCH", path, b),
  del:   path       => request("DELETE", path),
  upload,
  download,
  putPart,
};
